import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Hook to check maintenance status from Firestore
 * @param {string} userRole - The user's role (parent, admin, etc.)
 * @returns {Object} - Maintenance status and config
 */
const useMaintenanceStatus = (userRole = 'parent') => {
  const [maintenanceStatus, setMaintenanceStatus] = useState({
    isLoading: true,
    isMaintenanceMode: false,
    config: null,
    error: null
  });

  console.log('🔧 useMaintenanceStatus - Hook initialized with userRole:', userRole);

  useEffect(() => {
    console.log('🔧 useMaintenanceStatus - useEffect triggered with userRole:', userRole);
    
    // If userRole is null, we're still waiting for user data - stay in loading state
    if (userRole === null) {
      console.log('🔧 useMaintenanceStatus - userRole is null, staying in loading state');
      setMaintenanceStatus({
        isLoading: true,
        isMaintenanceMode: false,
        config: null,
        error: null
      });
      return () => {}; // Return empty cleanup function
    }
    
    const publicConfigRef = doc(db, 'system', 'publicConfig');
    
    const unsubscribe = onSnapshot(
      publicConfigRef, 
      (docSnapshot) => {
        console.log('🔧 useMaintenanceStatus - onSnapshot triggered');
        console.log('🔧 useMaintenanceStatus - docSnapshot.exists():', docSnapshot.exists());
        
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          console.log('🔧 useMaintenanceStatus - Raw Firestore data:', JSON.stringify(data, null, 2));
          
          const maintenanceConfig = data.maintenance;
          console.log('🔧 useMaintenanceStatus - maintenanceConfig:', JSON.stringify(maintenanceConfig, null, 2));
          
          // Determine if maintenance mode is enabled based on user role
          // Developer bypasses ALL maintenance
          // parishAdmin, globalAdmin, assistant are blocked ONLY by admin maintenance
          // parent is blocked ONLY by parent maintenance
          let relevantConfig;
          let isMaintenanceModeActive = false;
          
          console.log('🔧 useMaintenanceStatus - Checking userRole:', userRole);
          
          if (userRole === 'developer') {
            console.log('🔧 useMaintenanceStatus - User is developer - bypassing ALL maintenance');
            // Developers bypass all maintenance modes
            relevantConfig = null;
            isMaintenanceModeActive = false;
          } else if (['parishAdmin', 'globalAdmin', 'assistant'].includes(userRole)) {
            console.log('🔧 useMaintenanceStatus - User is admin type - checking ONLY admin maintenance');
            // Admin users affected ONLY by admin maintenance (NOT parent maintenance)
            relevantConfig = maintenanceConfig?.admins;
            isMaintenanceModeActive = relevantConfig?.enabled || false;
            console.log('🔧 useMaintenanceStatus - Admin maintenance config:', JSON.stringify(relevantConfig, null, 2));
            console.log('🔧 useMaintenanceStatus - Admin maintenance enabled:', isMaintenanceModeActive);
          } else {
            console.log('🔧 useMaintenanceStatus - User is parent type - checking ONLY parent maintenance');
            // Parent users affected ONLY by parent maintenance (NOT admin maintenance)
            relevantConfig = maintenanceConfig?.parents;
            isMaintenanceModeActive = relevantConfig?.enabled || false;
            console.log('🔧 useMaintenanceStatus - Parent maintenance config:', JSON.stringify(relevantConfig, null, 2));
            console.log('🔧 useMaintenanceStatus - Parent maintenance enabled:', isMaintenanceModeActive);
          }
          
          const finalStatus = {
            isLoading: false,
            isMaintenanceMode: isMaintenanceModeActive,
            config: relevantConfig,
            isAdmin: ['parishAdmin', 'globalAdmin', 'assistant', 'developer'].includes(userRole),
            error: null
          };
          
          console.log('🔧 useMaintenanceStatus - Final maintenance status:', JSON.stringify(finalStatus, null, 2));
          
          setMaintenanceStatus(finalStatus);
        } else {
          console.log('🔧 useMaintenanceStatus - Document does not exist - no maintenance');
          // Document doesn't exist, assume no maintenance
          setMaintenanceStatus({
            isLoading: false,
            isMaintenanceMode: false,
            config: null,
            isAdmin: false,
            error: null
          });
        }
      },
      (error) => {
        console.error('🔧 useMaintenanceStatus - Error fetching maintenance status:', error);
        // If it's a permission error, still allow the app to continue without maintenance mode
        if (error.code === 'permission-denied') {
          console.log('🔧 useMaintenanceStatus - Permission denied, assuming no maintenance');
          setMaintenanceStatus({
            isLoading: false,
            isMaintenanceMode: false,
            config: null,
            isAdmin: false,
            error: 'permission-denied'
          });
        } else {
          setMaintenanceStatus({
            isLoading: false,
            isMaintenanceMode: false,
            config: null,
            isAdmin: false,
            error: error.message
          });
        }
      }
    );

    return () => {
      console.log('🔧 useMaintenanceStatus - Unsubscribing from maintenance status');
      unsubscribe();
    };
  }, [userRole]);

  return maintenanceStatus;
};

export default useMaintenanceStatus;
